import EE from 'events';
import ScenariosTrackingService from '~/actions/scenarios/ScenariosTrackingService';
import { initClientEventApi } from '~/clientEvent';
import getLogTime from '~/helpers/getLogTime';
import EventTransportService from '~/listenerEventService/EventTransportService';
import ClientEvents from '~/listenerEventService/listeners/clientEvents';
import DomListener from '~/listenerEventService/listeners/dom';
import FieldListener from '~/listenerEventService/listeners/field';
import FormListener from '~/listenerEventService/listeners/form';
import LocationListener from '~/listenerEventService/listeners/hash';
import IdleListener from '~/listenerEventService/listeners/idle';
import MapListener from '~/listenerEventService/listeners/map';
import MouseListener from '~/listenerEventService/listeners/mouse';
import ScreenListener from '~/listenerEventService/listeners/screen';
import ScrollListener from '~/listenerEventService/listeners/scroll';
import TimeListener from '~/listenerEventService/listeners/time';
import UserInfoListener from '~/listenerEventService/listeners/userinfo';
import { clickstreamLogger } from '~/logging';
import settings from '~/utils/Settings';

// Stop listeners one by one during message queue overflow and start one by one when overflow mode is canceled
const subscriptions = [];
// [{'listener': {}, 'name': name, 'priority': number}, {'listener': {}, 'name': name, 'priority': number}]
const listenerInfos = [];
const stoppedListenerInfos = [];

let isOverflow = false;

let userInfoListener = null;
let mouseListenerInfo = null;
let formListenerInfo = null;
let scrollListenerInfo = null;
let screenListenerInfo = null;
let hashListenerInfo = null;
let domListenerInfo = null;
let fieldListenerInfo = null;
let mapListenerInfo = null;
let timeListenerInfo = null;
let idleListenerInfo = null;
let clientEvents = null;

function subscribeAll(eventName, callback) {
  for (const listenerInfo of listenerInfos) {
    listenerInfo.listener.on(eventName, callback);
  }
  subscriptions.push({
    eventName,
    callback,
  });
}

// Remove all subscriptions for all listeners
function unsubscribeAll() {
  for (const listenerInfo of listenerInfos) {
    for (const subscription of subscriptions) {
      listenerInfo.listener.off(subscription.eventName);
    }
  }
  // Clear array
  subscriptions.splice(0, subscriptions.length);
}

async function processQueueOverflow(message) {
  clickstreamLogger.error(
    `Message queue is overloaded, current size: ${message.queueSize}, overflow queue size: ${message.overflowQueueSize}`
  );

  isOverflow = true;
  // If during stopping listeners overflow mode is changed, cancel stopping listeners
  while (listenerInfos.length > 0 && isOverflow) {
    stopOneListener();
    // Wait timeout to next stop
    if (listenerInfos.length > 0) {
      clickstreamLogger.debug(
        `Waiting for next listener to stop, overflowMode: ${isOverflow}`
      );
    } else {
      clickstreamLogger.debug(
        `All listeners were stopped, overflowMode: ${isOverflow}`
      );
    }
    await timeout(this.stopListenersInterval);
  }
}

async function processCancelQueueOverflow(message) {
  clickstreamLogger.error(
    `Message queue length is back to normal, current length: ${message.queueSize}`
  );

  isOverflow = false;
  // If during starting listeners overflow mode is changed, stop starting listeners
  while (stoppedListenerInfos.length > 0 && !isOverflow) {
    startOneListener();
    if (stoppedListenerInfos.length > 0) {
      // Wait timeout to next start
      clickstreamLogger.debug(
        `Waiting for next listener to start, overflowMode: ${isOverflow}`
      );
    } else {
      clickstreamLogger.debug(
        `All listeners were started, overflowMode: ${isOverflow}`
      );
    }
    await timeout(this.startListenersInterval);
  }
}

// Add listeners to active listeners array
function addAllToActive(listenersToActivate) {
  listenerInfos.length = 0;
  for (const listenerInfo of listenersToActivate) {
    // Checking if listener configuration has listener field only once
    if (listenerInfo && listenerInfo.listener) {
      listenerInfos.push(listenerInfo);
    }
  }
  listenerInfos.sort((a, b) => a.priority - b.priority);
}

// Start listener with max priority
function startOneListener() {
  if (stoppedListenerInfos.length === 0) {
    return;
  }
  // Array is always sorted, max priority element is always the last, get it and then remove
  const startedListenerInfo = stoppedListenerInfos.pop();
  listenerInfos.unshift(startedListenerInfo);
  // Subscribe listener to all subscriptions
  for (const subscription of subscriptions) {
    startedListenerInfo.listener.on(
      subscription.eventName,
      subscription.callback
    );
  }
  startedListenerInfo.listener.start();
  clickstreamLogger.debug(
    `Listener ${startedListenerInfo.name} with ${
      startedListenerInfo.priority
    } priority was started, overflowMode: ${isOverflow}, time: ${getLogTime()}`
  );
}

// Stop listener with min priority
function stopOneListener() {
  if (listenerInfos.length === 0) {
    return;
  }
  // Array is always sorted, min priority element is always the first, get it and then remove
  const stoppedListenerInfo = listenerInfos.shift();
  stoppedListenerInfo.listener.stop();
  // Unsubscribe listener from all subscriptions
  for (const subscription of subscriptions) {
    stoppedListenerInfo.listener.off(
      subscription.eventName,
      subscription.callback
    );
  }
  stoppedListenerInfos.push(stoppedListenerInfo);
  clickstreamLogger.debug(
    `Listener ${stoppedListenerInfo.name} with ${
      stoppedListenerInfo.priority
    } priority was stopped, overflowMode: ${isOverflow}, time: ${getLogTime()}`
  );
}

function processError(error) {
  clickstreamLogger.error('ListenerService error: ', error);
}

function timeout(millis) {
  return new Promise(resolve => setTimeout(resolve, millis));
}

class ListenerService extends EE {
  constructor() {
    super();
    processQueueOverflow = processQueueOverflow.bind(this);
    processCancelQueueOverflow = processCancelQueueOverflow.bind(this);
  }

  get stopListenersInterval() {
    return settings.getOrDefault('STOP_LISTENERS_INTERVAL', 1000);
  }

  get startListenersInterval() {
    return settings.getOrDefault('START_LISTENERS_INTERVAL', 1000);
  }

  /**
   * listenerInfos = [{'listener': {}, 'name': name, 'priority': number}]
   * The more number the more priority and less chances to stop listener
   * @param {*} enableTrackingEvents
   * @param {*} enableTrackingScenarios
   */
  start(enableTrackingEvents, enableTrackingScenarios) {
    addAllToActive([
      (mouseListenerInfo = {
        listener: new MouseListener(),
        name: 'mouseListener',
        priority: 3,
      }),
      (scrollListenerInfo = {
        listener: new ScrollListener(),
        name: 'scrollListener',
        priority: 5,
      }),
      (clientEvents = {
        listener: new ClientEvents(),
        name: 'clientEvents',
        priority: 1,
      }),
      (formListenerInfo = {
        listener: new FormListener(),
        name: 'formListener',
        priority: 7,
      }),
      (screenListenerInfo = {
        listener: new ScreenListener(),
        name: 'screenListener',
        priority: 13,
      }),
      (hashListenerInfo = {
        listener: new LocationListener(),
        name: 'hashListener',
        priority: 17,
      }),
      (domListenerInfo = {
        listener: new DomListener(),
        name: 'domListener',
        priority: 19,
      }),
      (fieldListenerInfo = {
        listener: new FieldListener(),
        name: 'fieldListener',
        priority: 21,
      }),
      (mapListenerInfo = {
        listener: new MapListener(),
        name: 'mapListener',
        priority: 21,
      }),
      (timeListenerInfo = {
        listener: new TimeListener(),
        name: 'timeListener',
        priority: 23,
      }),
      (idleListenerInfo = {
        listener: new IdleListener(),
        name: 'idleListener',
        priority: 23,
      }),
      (userInfoListener = {
        listener: new UserInfoListener(),
        name: 'userInfoListener',
        priority: 34,
      }),
    ]);

    if (enableTrackingEvents) {
      EventTransportService.start();
      subscribeAll('analytics', EventTransportService.send);
      EventTransportService.on('overflow', processQueueOverflow);
      EventTransportService.on('cancel-overflow', processCancelQueueOverflow);
      EventTransportService.on('error', processError);
    }
    if (enableTrackingScenarios) {
      ScenariosTrackingService.start();
      subscribeAll('analytics', ScenariosTrackingService.check);
    }

    // Start all active listeners
    try {
      for (const listenerInfo of listenerInfos) {
        listenerInfo.listener.start();
      }
    } catch (error) {
      clickstreamLogger.debug('Error in Start all active listeners', error);
    }
    
    // Инициализируем сервис клиентских событий
    initClientEventApi();
  }

  stop() {
    // Stop all active listeners
    for (const listenerInfo of listenerInfos) {
      listenerInfo.listener.stop();
    }
    unsubscribeAll();
    transportService.stop();
    ScenariosTrackingService.stop();
    // Clear arrays
    subscriptions.splice(0, subscriptions.length);
    listenerInfos.splice(0, listenerInfos.length);
    stoppedListenerInfos.splice(0, stoppedListenerInfos.length);
    isOverflow = false;
  }
}

export default new ListenerService();
